import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
import apiClient from '../../../api/client';
import PhoneVerificationModal from '../../../components/phone-verification-modal';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '../../../components/ui/input-otp';
import { FaceVerifyScreens } from '../../../constants/face.const';
import { useApp as portalApp } from '../../../context/usePortal';

enum PinSetFlowStep {
  EnterNewPin,
  ConfirmPin,
  Success
}

const formSchema = z.object({
  newPin: z.string().min(6, 'PIN must be at least 6 characters'),
  confirmPin: z.string().min(6, 'PIN must be at least 6 characters')
});

type FormSchema = z.infer<typeof formSchema>;

interface IProps {
  onSuccess: () => void;
}

const SetPin: React.FC<IProps> = ({ onSuccess }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [verifyPhone, setVerifyPhone] = useState<boolean>(false);
  const { user } = portalApp();
  const [step, setStep] = useState(PinSetFlowStep.EnterNewPin);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newPin: '',
      confirmPin: ''
    }
  });

  const phoneNumber = `${user?.country_code}-${user?.user_phone_number}`;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setStep(PinSetFlowStep.EnterNewPin);
    setVerifyPhone(false);
    form.reset();
  };

  const handleNewPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }
    setStep(PinSetFlowStep.ConfirmPin);
  };

  const handleConfirmPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }
    onSubmit(form.getValues());
  };

  const onSubmit = (data: FormSchema) => {
    if (data.newPin !== data.confirmPin) {
      form.setError('confirmPin', {
        type: 'manual',
        message: 'PINs do not match'
      });
      return;
    }
    setVerifyPhone(true);
  };

  const setPin = useMutation({
    mutationFn: async ({ newPin, otp }: { newPin: string; otp: string }) => {
      await apiClient.post('/users/set-pin', { newPin, otp });
    },
    onSuccess: () => {
      toast.success('PIN set successfully');
      setVerifyPhone(false);
      setStep(PinSetFlowStep.Success);
      onSuccess();
    },
    onError: (error) => {
      console.error('Error setting PIN:', error);
      if (
        (error as any)?.response?.data?.error?.code === 'OTP_FAILED_ATTEMPTS'
      ) {
        return;
      }
      toast.error(
        (error as any)?.response?.data?.error?.message ||
          'Failed to set PIN. Please try again.'
      );
    }
  });

  const handleSubmitSetPin = (otp: string) => {
    setPin.mutate({ newPin: form.getValues().newPin, otp });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-gray-900">Set PIN</h3>
          <p className="text-sm text-gray-500">
            Create a PIN to secure transactions and enable payment limits.
          </p>
        </div>
        <Button
          type="button"
          className="min-w-24"
          onClick={() => setOpenDialog(true)}
        >
          Set
        </Button>
      </div>

      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          {(step === PinSetFlowStep.EnterNewPin ||
            step === PinSetFlowStep.ConfirmPin) && (
            <DialogHeader className="mt-5">
              <DialogTitle className="text-center">
                {step === PinSetFlowStep.EnterNewPin
                  ? 'Please Enter New 6-Digit PIN'
                  : 'Enter New PIN Again'}
              </DialogTitle>
            </DialogHeader>
          )}

          {step !== PinSetFlowStep.Success ? (
            <>
              <p className="text-center text-sm text-foreground/90">
                Please {step === PinSetFlowStep.EnterNewPin ? '' : 're-'}
                enter your 6-digit PIN
              </p>

              <form className="mb-10 flex flex-col items-center">
                {step === PinSetFlowStep.EnterNewPin && (
                  <>
                    <Controller
                      name={'newPin'}
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          {...field}
                          maxLength={6}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            handleNewPin(e);
                          }}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {form.formState.errors.newPin && (
                      <span className="mt-2 text-xs text-red-500">
                        {form.formState.errors.newPin.message as string}
                      </span>
                    )}
                  </>
                )}

                {step === PinSetFlowStep.ConfirmPin && (
                  <>
                    <Controller
                      name={'confirmPin'}
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          {...field}
                          maxLength={6}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            handleConfirmPin(e);
                          }}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {form.formState.errors.confirmPin && (
                      <span className="mt-2 text-xs text-red-500">
                        {form.formState.errors.confirmPin.message as string}
                      </span>
                    )}
                  </>
                )}
              </form>

              {step === PinSetFlowStep.ConfirmPin && (
                <Button
                  variant={'link'}
                  className="decoration-[#5B5959]"
                  onClick={() => {
                    form.reset();
                    setStep(PinSetFlowStep.EnterNewPin);
                  }}
                >
                  <p className="text-[#5B5959]">Start Again?</p>
                </Button>
              )}
            </>
          ) : (
            <div className="mb-1 mt-3 flex flex-col items-center">
              <img
                src="/assets/images/update-phone-success.png"
                alt="PIN Set Success"
                className="mx-auto w-32"
              />

              <h3 className="mt-4 text-center text-sm font-medium">
                Your PIN has been set successfully
              </h3>

              <Button
                className="mx-4 mt-6 w-4/5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
                onClick={handleCloseDialog}
                type="button"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PhoneVerificationModal
        phoneNumber={phoneNumber}
        onClose={() => setVerifyPhone(false)}
        onVerify={(otp) => handleSubmitSetPin(otp)}
        isOpen={verifyPhone}
        verifyError={setPin.error as Error | null}
        screen={FaceVerifyScreens.RESET_PIN}
        disableCannotAccessOldPhone={true}
      />
    </>
  );
};

export default SetPin;
